import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';
import { prefersReducedMotion } from '../../animations/usePrefersReducedMotion';

/**
 * Auto-advancing carousel — the landing page's centerpiece "alive" element.
 * Smooth slide transition (transform, not a hard cut), dot pagination,
 * arrow override, pauses on hover/touch, swipeable on mobile. Built
 * dependency-free (no embla/swiper) since this is the only place in the
 * app that needs one and the interaction is simple enough to own directly.
 *
 * Reduced-motion: auto-advance is disabled entirely (a visitor who's asked
 * for less motion shouldn't have slides changing under them on their own),
 * and the slide transform loses its transition duration so switching slides
 * via the dots/arrows is an instant cut rather than an animated slide.
 * Manual navigation still works either way.
 */
export default function Carousel({ slides, intervalMs = 5500, className }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const count = slides.length;
  const reducedMotion = prefersReducedMotion();

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused || count <= 1 || reducedMotion) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % count), intervalMs);
    return () => window.clearInterval(timer);
  }, [paused, count, intervalMs, reducedMotion]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setPaused(true);
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > 50) {
      if (touchDeltaX.current < 0) next();
      else prev();
    }
    touchStartX.current = null;
    setPaused(false);
  };

  return (
    <div
      className={clsx('relative', className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="overflow-hidden rounded-2xl">
        <div
          className={clsx('flex ease-out', reducedMotion ? 'duration-0' : 'transition-transform duration-500')}
          style={{ transform: `translateX(-${index * 100}%)` }}
          aria-live="polite"
        >
          {slides.map((slide, i) => (
            <div key={slide.id ?? i} className="w-full shrink-0" aria-hidden={i !== index}>
              {slide.content}
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-background-surface/90 border border-border text-foreground-muted hover:text-foreground shadow-card transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-background-surface/90 border border-border text-foreground-muted hover:text-foreground shadow-card transition-colors"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>

          <div className="flex items-center justify-center gap-2 mt-5">
            {slides.map((slide, i) => (
              <button
                key={slide.id ?? i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                className={clsx(
                  'h-2 rounded-full transition-all duration-300',
                  i === index ? 'w-6 bg-accent' : 'w-2 bg-border-strong hover:bg-foreground-subtle'
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
